/**
 * QR code du profil (migration 0334) : code de connexion personnel. Scanner
 * le QR de quelqu'un relie les deux comptes IMMÉDIATEMENT selon les rôles
 * (élève ⇄ élève : camarades, élève ⇄ prof, élève ⇄ parent).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

/** Origine FIXE du lien du QR (jamais `capacitor://` en natif). */
export const CONNECT_PUBLIC_ORIGIN = 'https://app.jeraisonne.fr';

const CODE_RE = /^[A-HJKMNP-Z2-9]{8}$/;

export type ConnectRelation = 'peer' | 'tutor' | 'parent';

export function connectUrl(code: string): string {
  return `${CONNECT_PUBLIC_ORIGIN}/connexion/${code}`;
}

/** Code saisi ou lien collé (« …/connexion/ABCD2345 », « abcd-2345 ») → code, sinon null. */
export function normalizeConnectCode(input: string): string | null {
  const fromUrl = input.match(/\/connexion\/([A-Za-z0-9-]+)/)?.[1] ?? input;
  const c = fromUrl.toUpperCase().replace(/[\s-]/g, '');
  return CODE_RE.test(c) ? c : null;
}

/** « ABCD2345 » → « ABCD-2345 ». */
export function formatConnectCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

/** Ce que le lien va faire, vu de la personne qui scanne. */
export function connectRelationLabel(relation: ConnectRelation, otherRole: string): string {
  if (relation === 'peer')
    return 'Vous serez camarades : vous pourrez vous défier sur les SKILLCARDS.';
  if (relation === 'tutor') {
    return otherRole === 'student'
      ? 'Cet élève sera relié à toi : tu suivras sa progression.'
      : 'Ce prof sera relié à toi : il suivra ta progression et pourra te donner des exercices.';
  }
  return otherRole === 'student'
    ? 'Cet enfant sera relié à ton compte parent.'
    : 'Ce parent sera relié à ton compte : il suivra ta progression.';
}

export function connectErrorMessage(code: string): string {
  switch (code) {
    case 'not_found':
      return 'Ce code n’existe pas ou a été renouvelé. Demande le nouveau QR code.';
    case 'self':
      return 'C’est ton propre code !';
    case 'unsupported':
      return 'Ces deux comptes ne peuvent pas être reliés (il faut un élève d’un côté).';
    case 'account_required':
      return 'Connecte-toi avec ton compte pour te relier.';
    default:
      return 'La connexion n’a pas abouti. Réessaie.';
  }
}

/** Page des contacts du rôle (après une connexion réussie). */
export function contactsPathFor(role: string | null | undefined): string {
  if (role === 'tutor' || role === 'admin') return '/tutor/contacts';
  if (role === 'parent') return '/parent/children';
  if (role === 'player') return '/player/dashboard';
  return '/student/contacts';
}

export const myConnectCodeKey = (userId: string) => ['connect-code', userId] as const;

export function useMyConnectCode(userId: string | null | undefined) {
  return useQuery({
    queryKey: myConnectCodeKey(userId ?? ''),
    enabled: !!userId,
    staleTime: Infinity,
    queryFn: async (): Promise<string> => {
      const { data, error } = await db.rpc('ensure_connect_code');
      if (error) throw new Error(error.message);
      return data as string;
    },
  });
}

export function useRenewConnectCode(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await db.rpc('renew_connect_code');
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: (code) => qc.setQueryData(myConnectCodeKey(userId), code),
  });
}

export interface ConnectProfile {
  status: 'ok' | 'not_found' | 'self';
  first_name?: string | null;
  avatar_url?: string | null;
  role?: string;
  relation?: ConnectRelation | null;
}

export function useConnectProfile(code: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['connect-profile', code],
    enabled: !!code && enabled,
    queryFn: async (): Promise<ConnectProfile> => {
      const { data, error } = await db.rpc('get_connect_profile', { p_code: code });
      if (error) throw new Error(error.message);
      return data as ConnectProfile;
    },
  });
}

export function useConnectWithCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data, error } = await db.rpc('connect_with_code', { p_code: code });
      if (error) throw new Error('network');
      const r = data as { ok?: boolean; error?: string; relation?: ConnectRelation };
      if (!r?.ok) throw new Error(r?.error ?? 'unknown');
      return r;
    },
    // Toutes les listes de contacts peuvent avoir changé.
    onSuccess: () => void qc.invalidateQueries(),
  });
}
